
import { PureComponent } from 'react';

import {
    createRecordingDialogEvent,
    sendAnalytics
} from '../../../analytics';
import { MHRecordingConstants } from '../../../base/lib-meet-hour';
import { RECORDING_STOP_REQUESTED } from '../../actionTypes';
import { hidePendingRecordingNotification } from '../../actions';
import { getActiveSession, stopRecordingSafely } from '../../functions';

/**
 * The type of the React {@code Component} props of
 * {@link AbstractStopRecordingDialog}.
 */
export type Props = {

    /**
     * The {@code MHConference} for the current conference.
     */
    _conference: Object,

    /**
     * The redux representation of the recording session to be stopped.
     */
    _fileRecordingSession: Object,

    /**
     * The redux {@code dispatch} function.
     */
    dispatch: Function,

    /**
     * Invoked to obtain translated strings.
     */
    t: Function
};

/**
 * Abstract React Component for getting confirmation to stop a file recording
 * session in progress.
 *
 * @augments Component
 */
export default class AbstractStopRecordingDialog<P: Props>
    extends PureComponent<P> {
    /**
     * Initializes a new {@code AbstrStopRecordingDialog} instance.
     *
     * @inheritdoc
     */
    constructor(props: P) {
        super(props);

        // Bind event handler so it is only bound once for every instance.
        this._onSubmit = this._onSubmit.bind(this);
    }

    _onSubmit: () => boolean;

    /**
     * Stops the recording session.
     *
     * @private
     * @returns {boolean} - True (to note that the modal should be closed).
     */
    _onSubmit() {
        sendAnalytics(createRecordingDialogEvent('stop', 'confirm.button'));

        const { _conference, _fileRecordingSession, dispatch } = this.props;

        if (_fileRecordingSession) {
            dispatch({
                type: RECORDING_STOP_REQUESTED,
                sessionId: _fileRecordingSession.id
            });

            // Dismiss the "preparing to record" banner immediately in case the
            // session is still in the PENDING state.
            dispatch(hidePendingRecordingNotification(_fileRecordingSession.mode));

            if (_conference) {
                stopRecordingSafely(_conference, _fileRecordingSession.id);
                _conference.sendCommand('recording-stop-requested', {
                    value: _fileRecordingSession.id
                });
            }
        }

        return true;
    }
}

/**
 * Maps (parts of) the Redux state to the associated props for the
 * {@code StopRecordingDialog} component.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {{
 *     _conference: MHConference,
 *     _fileRecordingSession: Object
 * }}
 */
export function _mapStateToProps(state: Object) {
    return {
        _conference: state['features/base/conference'].conference,
        _fileRecordingSession:
            getActiveSession(state, MHRecordingConstants.mode.FILE)
    };
}
